import React from 'react'
import dayjs from 'dayjs'
import type { CallingSessionSupportersShowQueryType } from '@frontend/graphql/queries/calling_sessions/supporters'
import { scopedTranslation } from '@frontend/utils/I18n'
import { useDialog } from '@components/DialogProvider'

import Button from '@components/atoms/Button'
import type { IconTypes } from '@components/utils/Icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@atoms/Tooltip'
import { CopiableValue } from '@molecules/CopiableValue'
import { SupporterTagsList } from '@molecules/SupporterTagsList'
import { SupporterStatusBadge, type StatusType } from './SupporterStatusBadge'
import { usePrioritiseSupporter } from '../hooks/usePrioritiseSupporter'
import { useSkipSupporter } from '../hooks/useSkipSupporter'

const t = scopedTranslation('components.supporter_details')
const tDemoSite = scopedTranslation('shared.demo_site')

function ContactInfoInline({ phone, email }: { phone: string | null | undefined; email: string | null | undefined }) {
  if (!phone && !email) return null

  return (
    <span
      className="tw:inline-flex tw:flex-wrap tw:gap-x-3 tw:gap-y-1 tw:text-sm tw:text-gray-500"
      data-testid="contact-info-row"
    >
      {phone && (
        <CopiableValue icon="phone-call-01" value={phone} copyLabel={t('contact_info.copy_phone')} testId="phone" />
      )}
      {email && <CopiableValue icon="mail-01" value={email} copyLabel={t('contact_info.copy_email')} testId="email" />}
    </span>
  )
}

type SupporterSummaryCardProps = {
  callingSessionSupporter: CallingSessionSupportersShowQueryType['callingSessionSupporter']
  isNextToCall?: boolean
  showEmail?: boolean
  showPhone?: boolean
  canSendIndividualSms?: boolean
  canSendIndividualEmail?: boolean
  demoSite?: boolean
}

type SendActionButtonProps = {
  enabled: boolean
  label: string
  icon: IconTypes
  disabledTooltip: string
  onClick: () => void
}

function SendActionButton({ enabled, label, icon, disabledTooltip, onClick }: SendActionButtonProps) {
  if (enabled) {
    return (
      <Button onClick={onClick} rank="secondary" color="primary" size="sm" leadingIcon={icon}>
        {label}
      </Button>
    )
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="tw:inline-flex" tabIndex={0}>
            <Button rank="secondary" color="primary" size="sm" leadingIcon={icon} disabled ariaLabel={label}>
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function SupporterSummaryCard({
  callingSessionSupporter,
  isNextToCall = false,
  showEmail = false,
  showPhone = false,
  canSendIndividualSms = true,
  canSendIndividualEmail = true,
  demoSite = false,
}: SupporterSummaryCardProps) {
  const { open: openDialog } = useDialog()
  const supporter = callingSessionSupporter?.supporter
  const { prioritise, isLoading } = usePrioritiseSupporter({
    alertsPath: 'supporterDetails',
    reloadProps: ['callingSessionSupporter'],
  })
  const { unskip, isLoading: isSkipLoading } = useSkipSupporter({
    alertsPath: 'supporterDetails',
    reloadProps: ['callingSessionSupporter'],
  })

  if (!supporter) {
    return null
  }

  const supporterName = supporter.fullName ?? `${supporter.firstName ?? ''} ${supporter.lastName ?? ''}`.trim()
  const canPrioritise = callingSessionSupporter.status !== 'completed' && !isNextToCall
  const canSkip = callingSessionSupporter.status !== 'completed'
  const hasActiveLeaderSkip = callingSessionSupporter.status === 'skipped_by_leader'
  const sendDisabledTooltip = demoSite ? tDemoSite('sending_disabled') : t('actions.send_disabled')

  const handlePrioritise = async () => {
    await prioritise({
      callingSessionSupporterId: callingSessionSupporter.id,
      supporterName,
    })
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('not_available')
    try {
      const date = dayjs(dateString)
      if (!date.isValid()) return t('invalid_date')
      return date.format('D MMMM YYYY')
    } catch {
      return t('invalid_date')
    }
  }

  const getSupporterSinceDate = () => {
    const dateToUse = supporter.externalCreatedAt ?? supporter.createdAt
    return formatDate(dateToUse)
  }

  const getLastContactedDate = () => {
    return formatDate(supporter.lastContactedAt)
  }

  const getLocation = () => {
    const { city, state, zip } = supporter
    const parts: string[] = []

    if (city) parts.push(city)
    if (state) parts.push(state)
    if (zip) parts.push(zip)

    return parts.length > 0 ? parts.join(', ') : t('not_available')
  }

  const getFederalElectorate = () => {
    return supporter.federalDistrict ?? t('not_available')
  }

  return (
    <div
      className="tw:bg-white tw:rounded-lg tw:border tw:border-gray-200 tw:p-6 tw:mb-6"
      data-testid={`supporter-details-${supporter.id}`}
    >
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:mb-3">
        <div className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-3 tw:gap-y-1 tw:min-w-0">
          <h3 className="tw:text-lg tw:font-semibold tw:text-gray-900 tw:m-0">
            {supporter.fullName ?? `${supporter.firstName ?? ''} ${supporter.lastName ?? ''}`.trim()}
          </h3>
          <ContactInfoInline
            phone={showPhone ? (supporter.primaryMobile ?? supporter.primaryPhone) : null}
            email={showEmail ? supporter.email : null}
          />
        </div>
        <span className="tw:min-w-fit">
          <SupporterStatusBadge
            status={callingSessionSupporter.status as StatusType}
            unavailableReason={callingSessionSupporter.unavailableReason}
            unavailableUntil={callingSessionSupporter.unavailableUntil}
            activeSkip={callingSessionSupporter.activeSkip}
          />
        </span>
      </div>

      {supporter.tags?.nodes && supporter.tags.nodes.length > 0 && (
        <div className="tw:mb-3">
          <SupporterTagsList tags={supporter.tags.nodes} />
        </div>
      )}

      <div className="tw:pt-2 tw:mb-6">
        <dl className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-[max-content_1fr] tw:gap-x-3 tw:gap-y-1 tw:sm:gap-y-3 tw:items-center">
          {[
            { label: t('last_contacted_label'), content: getLastContactedDate() },
            { label: t('supporter_since_label'), content: getSupporterSinceDate() },
            { label: t('location_label'), content: getLocation() },
            { label: t('federal_electorate_label'), content: getFederalElectorate() },
            { label: t('background_note_label'), content: supporter.note ?? t('no_background_note'), isNote: true },
          ].map(({ label, content, isNote }, index, array) => {
            const isLast = index === array.length - 1
            return (
              <React.Fragment key={index}>
                <dt
                  className={`tw:text-xs tw:text-gray-blue-500 tw:uppercase tw:font-bold tw:flex ${isNote ? 'tw:sm:items-start' : 'tw:items-center'}`}
                >
                  {label}
                </dt>
                <dd
                  className={`tw:text-sm tw:text-gray-900 tw:m-0 tw:flex ${isNote ? 'tw:sm:items-start' : 'tw:items-center'} ${isLast ? 'tw:mb-0' : 'tw:mb-3'} tw:sm:mb-0`}
                >
                  {content}
                </dd>
              </React.Fragment>
            )
          })}
        </dl>
      </div>

      <div className="tw:border-t tw:border-gray-200 tw:pt-6">
        <div className="tw:flex tw:justify-between tw:flex-wrap tw:gap-3">
          <div className="tw:flex tw:gap-3 tw:flex-wrap">
            <Button
              onClick={() => {
                openDialog({
                  type: 'logContact',
                  dialogData: {
                    id: supporter.id,
                    firstName: supporter.firstName,
                    lastName: supporter.lastName,
                    groupId: callingSessionSupporter.actionGroupId,
                    callingSessionSupporterId: callingSessionSupporter.id,
                  },
                })
              }}
              rank="secondary"
              color="primary"
              size="sm"
              leadingIcon="pencil-02"
            >
              {t('actions.log_contact')}
            </Button>
            <SendActionButton
              enabled={canSendIndividualSms && !demoSite}
              label={t('actions.sms_supporter', { firstName: supporter.firstName })}
              icon="message-chat-square"
              disabledTooltip={sendDisabledTooltip}
              onClick={() => {
                openDialog({
                  type: 'sendSms',
                  dialogData: {
                    id: supporter.id,
                    firstName: supporter.firstName,
                    lastName: supporter.lastName,
                    groupId: callingSessionSupporter.actionGroupId,
                  },
                })
              }}
            />
            <SendActionButton
              enabled={canSendIndividualEmail && !demoSite}
              label={t('actions.email_supporter', { firstName: supporter.firstName })}
              icon="mail-01"
              disabledTooltip={sendDisabledTooltip}
              onClick={() => {
                openDialog({
                  type: 'sendEmail',
                  dialogData: {
                    id: supporter.id,
                    firstName: supporter.firstName,
                    lastName: supporter.lastName,
                    groupId: callingSessionSupporter.actionGroupId,
                  },
                })
              }}
            />
          </div>

          <div className="tw:flex tw:gap-3 tw:flex-wrap">
            <Button
              onClick={handlePrioritise}
              rank="link"
              size="sm"
              leadingIcon="arrow-circle-broken-right"
              disabled={!canPrioritise || isLoading}
            >
              {t('actions.call_next')}
            </Button>
            {hasActiveLeaderSkip ? (
              <Button
                onClick={() => unskip({ callingSessionSupporterId: callingSessionSupporter.id, supporterName })}
                rank="link"
                size="sm"
                leadingIcon="skip-back"
                disabled={isSkipLoading}
              >
                {t('actions.remove_skip')}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  openDialog({
                    type: 'skipSupporter',
                    dialogData: {
                      callingSessionSupporterId: callingSessionSupporter.id,
                      supporterName,
                    },
                  })
                }}
                rank="link"
                size="sm"
                leadingIcon="fast-forward"
                disabled={!canSkip || isSkipLoading}
              >
                {t('actions.skip_for')}
              </Button>
            )}
            <Button
              onClick={() => {
                openDialog({
                  type: 'updateContactPreferences',
                  dialogData: {
                    supporter: { id: supporter.id, firstName: supporter.firstName, lastName: supporter.lastName },
                    currentContactPreferences: {
                      stopReceivingEmails: false,
                      stopReceivingSms: false,
                      stopReceivingCalls: false,
                    },
                    groupId: callingSessionSupporter.actionGroupId,
                  },
                })
              }}
              rank="link"
              size="sm"
              leadingIcon="user-x"
              disabled={!callingSessionSupporter.actionGroupId}
            >
              {t('actions.manage_contact_preferences')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
